import React from "react";
import { Link } from "react-router-dom";
import { SEOHelmet } from "../components/common/SEOHelmet";
import {
  PATH_NETZVERBUND_SCHWARZWALD,
  PATH_ABOUT,
  PATH_GROUP_THERAPY,
} from "../utils/constants";
import {
  UsersIcon,
  PhoneIcon,
  EnvelopeIcon,
  HeartIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

const intro =
  "Der Netzverbund Schwarzwald ist ein regionales Versorgungsnetzwerk für Menschen mit komplexen psychischen Erkrankungen. Unser Ziel ist es, Betroffene auf ihrem individuellen Weg zu begleiten und ihnen eine bedarfsgerechte Unterstützung zu ermöglichen.";

const introDetail =
  "Dabei arbeiten verschiedene Fachdisziplinen, zum Beispiel Ärzte:Innen, Psychologen:Innen, Psychiater:Innen, Therapeuten:Innen und Einrichtungen eng zusammen, um eine möglichst umfassende und abgestimmte Versorgung sicherzustellen. Die Koordination wird zentral über die ERGOTHERAPIE UND ZEIT(T)RÄUME organisiert.";

const targetAudience =
  "Unser Angebot richtet sich an Menschen mit schweren psychischen Erkrankungen, die Unterstützung bei der Organisation ihrer Behandlung, im Alltag oder bei der Suche nach passenden Hilfsangeboten benötigen. Auch Angehörige sowie behandelnde Ärzt:Innen, Psycholog:Innen, Therapeut:Innen können sich an uns wenden.";

const supportItems = [
  "Orientierung im Hilfesystem",
  "Vermittlung geeigneter Unterstützungsangebote",
  "Koordination zwischen verschiedenen Behandlern",
  "Fragen zu Pflegegrad, Eingliederungshilfe und weiteren Sozialleistungen",
  "Vernetzung mit Beratungsstellen und Hilfsangeboten",
  "Klärung von Zuständigkeiten und Unterstützungsbedarfen",
];

const goalText =
  "Unser Ziel ist es, Menschen mit schweren psychischen Erkrankungen den Zugang zu passenden Hilfen zu erleichtern, Behandlungswege zu koordinieren und die Zusammenarbeit aller Beteiligten zu fördern. Dabei stehen die individuellen Bedürfnisse und Lebenssituationen der Klient:innen stets im Mittelpunkt.";

const backgroundText =
  "Gemeinsam mit mehreren Praxen in Rottweil haben wir einen genehmigten Netzverbund im Schwarzwald auf den Weg gebracht – bei der Umsetzung wurden wir vom IVP unterstützt. Als Praxis ERGOTHERAPIE UND ZEIT(T)RÄUME übernehmen wir die zentrale Koordination.";

const NETZVERBUND_PHONE = "074189698291";
const NETZVERBUND_PHONE_DISPLAY = "0741 89 69 82 91";
const NETZVERBUND_EMAIL = "info@netzverbund-schwarzwald.de";

const jsonLdDescription =
  "Regionales Versorgungsnetzwerk für Menschen mit komplexen psychischen Erkrankungen im Schwarzwald, zentral koordiniert über die ERGOTHERAPIE UND ZEIT(T)RÄUME in Rottweil.";

type ContentSectionProps = {
  id: string;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
};

const ContentSection: React.FC<ContentSectionProps> = ({ id, title, icon, children }) => (
  <section aria-labelledby={id} className="relative">
    <div className="flex items-start sm:items-center gap-4 sm:gap-6 mb-4">
      <div className="p-3 sm:p-4 rounded-xl bg-ergo-100/80 dark:bg-ergo-900/80 shadow-sm flex-shrink-0">
        <div className="text-ergo-600 dark:text-ergo-400">{icon}</div>
      </div>
      <h2
        id={id}
        className="text-xl sm:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white"
      >
        {title}
      </h2>
    </div>
    <div className="relative bg-white dark:bg-gray-800/60 rounded-xl sm:rounded-2xl shadow-sm border border-gray-200/60 dark:border-gray-700/40 p-6 sm:p-8 md:p-10">
      {children}
    </div>
  </section>
);

export default function NetzverbundSchwarzwaldPage() {
  return (
    <>
      <SEOHelmet page="netzverbundSchwarzwald" pathname={PATH_NETZVERBUND_SCHWARZWALD} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalOrganization",
          "name": "Netzverbund Schwarzwald",
          "description": jsonLdDescription,
          "url": "https://ergorw.de/netzverbund-schwarzwald",
          "telephone": `+49-741-89698291`,
          "email": NETZVERBUND_EMAIL,
          "areaServed": {
            "@type": "AdministrativeArea",
            "name": "Schwarzwald",
          },
          "memberOf": {
            "@type": "MedicalOrganization",
            "name": "Ergotherapie Rottweil - ERGOTHERAPIE UND ZEIT(T)RÄUME",
            "url": "https://ergorw.de",
          },
        })}
      </script>

      <main className="space-y-8 sm:space-y-14">
        <section aria-labelledby="netzverbund-title" className="relative">
          <h1
            id="netzverbund-title"
            className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6"
          >
            Netzverbund Schwarzwald
          </h1>
          <div className="space-y-3">
            <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">{intro}</p>
            <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">{introDetail}</p>
          </div>
        </section>

        <ContentSection
          id="netzverbund-target"
          title="Für wen ist der Netzverbund gedacht?"
          icon={<UsersIcon className="h-6 w-6" />}
        >
          <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed">{targetAudience}</p>
        </ContentSection>

        <ContentSection
          id="netzverbund-support"
          title="Welche Unterstützung bieten wir?"
          icon={<HeartIcon className="h-6 w-6" />}
        >
          <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
            Wir unterstützen unter anderem bei:
          </p>
          <ul className="space-y-2">
            {supportItems.map((item) => (
              <li key={item} className="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                • {item}
              </li>
            ))}
          </ul>
        </ContentSection>

        <ContentSection
          id="netzverbund-coordination"
          title="Koordination & Kontakt"
          icon={<PhoneIcon className="h-6 w-6" />}
        >
          <div className="space-y-4 sm:space-y-5">
            <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
              Frau Maier ist die Koordinatorin des Netzverbund Schwarzwald. Als Koordinatorin ist sie
              die Ansprechpartnerin bei Fragen rund um Ihre Versorgung und unterstützt die
              Zusammenarbeit zwischen den verschiedenen beteiligten Fachpersonen. Sie fungiert als
              Bindeglied zwischen Klient:innen, Psychiater:innen, Psycholog:innen, Ärzt:innen,
              Therapeut:innen sowie weiteren Hilfsangeboten.
            </p>
            <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
              Mehr über{" "}
              <Link
                to={PATH_ABOUT}
                className="font-medium text-ergo-700 dark:text-ergo-300 underline underline-offset-2 hover:text-ergo-800 dark:hover:text-ergo-200"
              >
                Susan Maier im Team
              </Link>
              .
            </p>

            <div className="rounded-xl border border-ergo-200/60 dark:border-ergo-700/40 bg-ergo-50/60 dark:bg-ergo-900/20 p-5 sm:p-6 space-y-3">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Telefonische Sprechzeiten
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                Montag bis Donnerstag: 09:00 Uhr bis 11:00 Uhr
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Telefon:{" "}
                <a
                  href={`tel:+49${NETZVERBUND_PHONE.slice(1)}`}
                  className="font-medium text-ergo-700 dark:text-ergo-300 underline underline-offset-2 hover:text-ergo-800 dark:hover:text-ergo-200"
                >
                  {NETZVERBUND_PHONE_DISPLAY}
                </a>
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2">
                <EnvelopeIcon className="h-5 w-5 flex-shrink-0 mt-0.5 text-ergo-600 dark:text-ergo-400" />
                <span>
                  E-Mail:{" "}
                  <a
                    href={`mailto:${NETZVERBUND_EMAIL}`}
                    className="font-medium text-ergo-700 dark:text-ergo-300 underline underline-offset-2 hover:text-ergo-800 dark:hover:text-ergo-200"
                  >
                    {NETZVERBUND_EMAIL}
                  </a>
                </span>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                Außerhalb der telefonischen Sprechzeiten wenden Sie sich bitte per E-Mail an uns
                oder hinterlassen Sie eine Nachricht auf dem Anrufbeantworter.
              </p>
            </div>
          </div>
        </ContentSection>

        <ContentSection
          id="netzverbund-advice"
          title="Beratung & Kosten"
          icon={<SparklesIcon className="h-6 w-6" />}
        >
          <div className="space-y-4 sm:space-y-5">
            <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
              Sie können sich auch dann an uns wenden, wenn Sie sich zunächst informieren möchten
              oder unsicher sind, welche Hilfen für Ihre Situation geeignet sind. Wir beraten Sie
              gerne. Beachten Sie auch unsere{" "}
              <Link
                to={PATH_GROUP_THERAPY}
                className="font-medium text-ergo-700 dark:text-ergo-300 underline underline-offset-2 hover:text-ergo-800 dark:hover:text-ergo-200"
              >
                Gruppenangebote
              </Link>
              , die wir über die Ergotherapie anbieten.
            </p>
            <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
              Die erste Kontaktaufnahme und Beratung durch die koordinative Stelle ist für
              Ratsuchende kostenfrei und umfasst maximal 10 Minuten. Gemeinsam besprechen wir Ihr
              Anliegen und überlegen, welche Unterstützung oder welche nächsten Schritte für Sie
              sinnvoll sein können.
            </p>
            <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
              Angehörige sind häufig wichtige Bezugspersonen und können sich bei Fragen oder
              Unterstützungsbedarf ebenfalls an uns wenden.
            </p>
          </div>
        </ContentSection>

        <ContentSection
          id="netzverbund-goal"
          title="Unser Ziel"
          icon={<UsersIcon className="h-6 w-6" />}
        >
          <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed">{goalText}</p>
        </ContentSection>

        <section aria-labelledby="netzverbund-background" className="relative">
          <h2
            id="netzverbund-background"
            className="text-lg sm:text-xl font-semibold tracking-tight text-ergo-600 dark:text-ergo-400 mb-3"
          >
            Hintergrund
          </h2>
          <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">{backgroundText}</p>
        </section>
      </main>
    </>
  );
}
